import { resolveMessageMedia, type ChatMessage } from '@teo/widget-shared';
import { resolveMessageReactKey } from './image-display-cache.js';
import { shortImageUrl } from './image-logger.js';

export type SyncLogLevel = 'debug' | 'info' | 'warn' | 'error';

const LOG_PREFIX = '[Teo Widget SYNC]';
const STORAGE_KEY = 'teo_widget_sync_debug';

/** Embed / bootstrap fallback. localStorage always wins when set. */
let embedEnabled: boolean | null = null;

function readLocalStorageFlag(): boolean | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === '1' || stored === 'true') return true;
    if (stored === '0' || stored === 'false') return false;
  } catch {
    // ignore
  }
  return null;
}

function readScriptFlag(): boolean {
  if (typeof document === 'undefined') return false;
  const script = document.querySelector<HTMLScriptElement>(
    'script[data-teo-initialized][data-sync-debug], script[data-slug][data-sync-debug]',
  );
  return (
    script?.dataset.syncDebug === 'true' || script?.dataset.syncDebug === ''
  );
}

function readDebugFlag(): boolean {
  const stored = readLocalStorageFlag();
  if (stored !== null) return stored;
  if (embedEnabled !== null) return embedEnabled;
  return readScriptFlag();
}

/**
 * Set fallback from embed bootstrap (`data-sync-debug` / env).
 * Does **not** write localStorage — live override:
 * `localStorage.setItem('teo_widget_sync_debug', '1')`
 */
export function setSyncDebugEnabled(enabled: boolean): void {
  embedEnabled = enabled;
}

export function isSyncDebugEnabled(): boolean {
  return readDebugFlag();
}

export function messageRenderFingerprint(message: ChatMessage): string {
  const { attachment, linkPreviews, caption } = resolveMessageMedia(message);
  return [
    resolveMessageReactKey(message),
    message.role,
    message.status ?? '',
    message.contentType ?? 'text',
    caption ?? message.text.trim(),
    attachment?.kind ?? '',
    attachment?.url ?? '',
    linkPreviews.map((preview) => preview.url).join(','),
  ].join('|');
}

export function summarizeChatMessage(message: ChatMessage): Record<string, unknown> {
  const { attachment, linkPreviews, caption } = resolveMessageMedia(message);
  return {
    key: resolveMessageReactKey(message),
    id: message.id,
    clientId: message.clientId,
    role: message.role,
    status: message.status,
    contentType: message.contentType,
    text: message.text.slice(0, 80),
    caption: caption?.slice(0, 80),
    attachmentKind: attachment?.kind,
    attachmentUrl: shortImageUrl(attachment?.url),
    previewCount: linkPreviews.length,
    fingerprint: messageRenderFingerprint(message),
  };
}

export type MessageListDiff = {
  prevCount: number;
  nextCount: number;
  sameArrayRef: boolean;
  added: Record<string, unknown>[];
  removed: Record<string, unknown>[];
  changed: Record<string, unknown>[];
  reusedRefs: number;
};

export function diffMessageLists(
  previous: ChatMessage[],
  next: ChatMessage[],
): MessageListDiff {
  const prevFp = previous.map(
    (message) => [messageRenderFingerprint(message), message] as const,
  );
  const nextFp = next.map(
    (message) => [messageRenderFingerprint(message), message] as const,
  );
  const prevByKey = new Map(prevFp);
  const nextByKey = new Map(nextFp);

  const added = nextFp
    .filter(([fp]) => !prevByKey.has(fp))
    .map(([, message]) => summarizeChatMessage(message));
  const removed = prevFp
    .filter(([fp]) => !nextByKey.has(fp))
    .map(([, message]) => summarizeChatMessage(message));
  const changed: Record<string, unknown>[] = [];

  for (const [, message] of nextFp) {
    const key = resolveMessageReactKey(message);
    const prev = previous.find(
      (row) => resolveMessageReactKey(row) === key || row.id === message.id,
    );
    if (!prev) continue;
    if (prev === message) continue;
    if (messageRenderFingerprint(prev) === messageRenderFingerprint(message)) {
      changed.push({
        key,
        kind: 'ref-changed-same-fingerprint',
        before: summarizeChatMessage(prev),
        after: summarizeChatMessage(message),
      });
      continue;
    }
    changed.push({
      key,
      kind: 'content-changed',
      before: summarizeChatMessage(prev),
      after: summarizeChatMessage(message),
    });
  }

  return {
    prevCount: previous.length,
    nextCount: next.length,
    sameArrayRef: previous === next,
    added,
    removed,
    changed,
    reusedRefs: next.filter((row) => previous.includes(row)).length,
  };
}

/** One-line summary for console (inline without expanding the diff object). */
export function formatMessageListDiff(diff: MessageListDiff): string {
  const changedParts = diff.changed.map((entry) => {
    const before = (entry.before as { status?: unknown } | undefined)?.status ?? '∅';
    const after = (entry.after as { status?: unknown } | undefined)?.status ?? '∅';
    return `${entry.key}:${entry.kind}(${String(before)}→${String(after)})`;
  });

  return [
    `count ${diff.prevCount}→${diff.nextCount}`,
    `reused=${diff.reusedRefs}`,
    `added=${diff.added.length}`,
    `removed=${diff.removed.length}`,
    `changed=${diff.changed.length}`,
    changedParts.length ? `[${changedParts.join(', ')}]` : '',
  ]
    .filter(Boolean)
    .join(' ');
}

export function logSync(
  scope: string,
  message: string,
  data?: unknown,
  level: SyncLogLevel = 'info',
): void {
  if (!readDebugFlag()) return;

  const label = `${LOG_PREFIX} [${scope}] ${message}`;
  const payload = data === undefined ? undefined : [label, data];

  switch (level) {
    case 'debug':
      if (payload) console.debug(...payload);
      else console.debug(label);
      break;
    case 'warn':
      if (payload) console.warn(...payload);
      else console.warn(label);
      break;
    case 'error':
      if (payload) console.error(...payload);
      else console.error(label);
      break;
    default:
      if (payload) console.info(...payload);
      else console.info(label);
  }
}
